import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const DPR_CAP = 1.5;

const VERTEX_SHADER = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.0,1.0);}';

const FRAGMENT_SHADER = `
precision highp float;
uniform float u_time;
uniform vec2  u_res;
uniform float u_grain;
uniform float u_relief;
uniform float u_nacre;
uniform float u_contrast;

/* integer permutation hash (Ashima webgl-noise style): exact fp32 arithmetic, identical on
   every GPU. fract(sin()) collapses into flat lattice cells on Mali/Adreno/Xclipse, and float
   hashes (Hoskins 4djSRW) have resonant dead cells on small lattice coordinates. */
float m289(float x){return x-floor(x*(1.0/289.0))*289.0;}
float permute(float x){x=m289(x);return m289((x*34.0+1.0)*x);}
float hash(vec2 p){return fract(permute(permute(p.x)+p.y)*(1.0/289.0));}
float noise(vec2 p){
  vec2 i=floor(p);vec2 f=fract(p);vec2 u=f*f*(3.0-2.0*f);
  return mix(mix(hash(i),hash(i+vec2(1.0,0.0)),u.x),
             mix(hash(i+vec2(0.0,1.0)),hash(i+vec2(1.0,1.0)),u.x),u.y);
}
float fbm(vec2 p){
  float v=0.0;float a=0.5;
  for(int i=0;i<5;i++){v+=a*noise(p);p=p*2.03+vec2(13.7,7.1);a*=0.5;}
  return v;
}

void main(){
  vec2 uv=gl_FragCoord.xy/u_res;
  float asp=u_res.x/u_res.y;
  /* arbitrary domain offset — acts as the pattern seed */
  vec2 p=vec2(uv.x*asp,uv.y)*1.55+vec2(23.7,11.3);
  float t=u_time*0.045;

  /* two-pass domain warping = fluid silk folds */
  vec2 q=vec2(fbm(p+t),fbm(p+vec2(5.2,1.3)-t*0.8));
  vec2 r=vec2(fbm(p+2.6*q+vec2(1.7,9.2)+0.15*t),fbm(p+2.6*q+vec2(8.3,2.8)-0.12*t));
  float f=fbm(p+2.4*r);

  /* ivory / greige / taupe / blush palette */
  vec3 ivory=vec3(0.930,0.916,0.892);
  vec3 greige=vec3(0.800,0.772,0.726);
  vec3 taupe=vec3(0.660,0.628,0.578);
  vec3 blush=vec3(0.892,0.832,0.786);
  vec3 col=mix(ivory,greige,smoothstep(0.15,0.92,f));
  col=mix(col,blush,0.32*smoothstep(0.35,0.85,q.x));
  col=mix(col,taupe,(0.42+0.30*u_contrast)*smoothstep(0.55-0.12*u_contrast,0.98,r.y));

  /* fbm derivative = fold shading */
  float f2=fbm(p+2.4*r+vec2(0.05,0.07));
  col+=(f2-f)*u_relief*0.55;

  /* satin sheen on the ridges */
  float sheen=pow(smoothstep(0.52,0.86,f),3.0);
  col+=sheen*(0.085+0.06*u_relief);

  /* subtle spectral iridescence */
  vec3 irid=0.5+0.5*cos(6.2831*(f*1.2+vec3(0.0,0.33,0.67)));
  col+=u_nacre*0.09*(irid-0.5)*smoothstep(0.45,0.9,f);

  col=mix(col,(col-0.87)*1.25+0.87,u_contrast*0.6);

  /* top-left directional light */
  col+=0.035*(1.0-uv.y)*(1.0-uv.x*0.5);

  /* vignette + photographic grain */
  float vig=1.0-0.16*pow(length(uv-0.5)*1.25,2.0);
  col*=vig;
  col+=u_grain*(hash(gl_FragCoord.xy+fract(u_time)*17.0)-0.5)*0.030;

  gl_FragColor=vec4(col,1.0);
}`;

/**
 * One WebGL context shared by every instance — browsers evict contexts
 * beyond ~8-16 per page. Each instance blits its frame into its own 2D canvas.
 */
const renderer = {
  /** @type {HTMLCanvasElement | null} */
  canvas: null,
  /** @type {WebGLRenderingContext | null} */
  gl: null,
  /** @type {WebGLProgram | null} */
  program: null,
  /** @type {WebGLBuffer | null} */
  buffer: null,
  /** @type {Record<string, any> | null} */
  uniforms: null,
  /** @type {Set<SilkHeroComponent>} */
  instances: new Set(),
  rafId: 0,
  startTime: 0,
  lost: false,

  ensure() {
    if (this.gl && !this.gl.isContextLost()) return true;

    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        this.lost = true;
      });
      this.canvas.addEventListener('webglcontextrestored', () => {
        this.lost = false;
        this.program = null;
        this.buffer = null;
        for (const instance of this.instances) instance.invalidate();
      });
    }

    const gl = this.canvas.getContext('webgl', {
      antialias: false,
      alpha: false,
      powerPreference: 'low-power',
    });
    if (!gl) return false;

    this.gl = gl;
    this.program = null;
    this.buffer = null;
    return true;
  },

  getProgram() {
    if (this.program) return this.program;

    const gl = this.gl;
    if (!gl) return null;

    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return null;
      return shader;
    };

    const vertex = compile(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertex || !fragment) return null;

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;

    this.uniforms = {
      position: gl.getAttribLocation(program, 'a'),
      time: gl.getUniformLocation(program, 'u_time'),
      resolution: gl.getUniformLocation(program, 'u_res'),
      grain: gl.getUniformLocation(program, 'u_grain'),
      relief: gl.getUniformLocation(program, 'u_relief'),
      nacre: gl.getUniformLocation(program, 'u_nacre'),
      contrast: gl.getUniformLocation(program, 'u_contrast'),
    };
    this.program = program;
    return program;
  },

  add(instance) {
    this.instances.add(instance);
    this.start();
  },

  remove(instance) {
    this.instances.delete(instance);
    if (this.instances.size === 0) this.stop();
  },

  start() {
    if (this.rafId || this.instances.size === 0) return;
    if (!this.startTime) this.startTime = performance.now();
    const tick = () => {
      this.rafId = requestAnimationFrame(tick);
      this.frame();
    };
    tick();
  },

  stop() {
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  },

  frame() {
    if (this.lost || !this.ensure()) {
      this.stop();
      return;
    }

    const now = performance.now();
    let animating = false;
    for (const instance of this.instances) {
      if (instance.draw(now)) animating = true;
    }
    if (!animating) this.stop();
  },
};

/**
 * Ambient "silk" WebGL background for the silk hero section.
 *
 * Renders behind the section content; the section background color acts as
 * the fallback when WebGL is unavailable or the GPU context is lost. With
 * reduced motion, a single static frame is rendered instead of the animation.
 *
 * @extends {Component}
 */
class SilkHeroComponent extends Component {
  requiredRefs = ['canvas'];

  static observedAttributes = ['speed', 'grain', 'relief', 'nacre', 'contrast'];

  /** @type {CanvasRenderingContext2D | null} */
  #context = null;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  #width = 0;
  #height = 0;
  #visible = true;
  #needsRender = true;
  #static = false;
  #settings = { speed: 0.4, grain: 1.65, relief: 1, nacre: 0.15, contrast: 0.35 };

  connectedCallback() {
    super.connectedCallback();

    this.#static = prefersReducedMotion();
    this.#readAttributes();
    this.#context = this.refs.canvas.getContext('2d');

    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);
    this.#resize();

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      this.#visible = entries[entries.length - 1]?.isIntersecting ?? true;
      if (this.#visible) renderer.start();
    });
    this.#intersectionObserver.observe(this);

    if (renderer.ensure()) renderer.add(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    renderer.remove(this);
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
  }

  attributeChangedCallback() {
    this.#readAttributes();
    this.invalidate();
  }

  invalidate() {
    this.#needsRender = true;
    renderer.start();
  }

  scrollToNextSection() {
    const next = this.closest('.shopify-section')?.nextElementSibling;
    if (!(next instanceof HTMLElement)) return;

    next.style.scrollMarginBlockStart = 'var(--header-height, 0px)';
    next.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  #resize() {
    const { canvas } = this.refs;
    const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    const width = Math.max(1, Math.round(this.clientWidth * dpr));
    const height = Math.max(1, Math.round(this.clientHeight * dpr));

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    this.#width = width;
    this.#height = height;
    this.invalidate();
  }

  #readAttributes() {
    const percent = (name, fallback) => {
      const value = Number.parseFloat(this.getAttribute(name) ?? '');
      return (Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : fallback) / 100;
    };

    this.#settings = {
      speed: percent('speed', 40),
      grain: percent('grain', 55) * 3,
      relief: percent('relief', 100),
      nacre: percent('nacre', 15),
      contrast: percent('contrast', 35),
    };
  }

  /**
   * Renders one frame from the shared WebGL canvas into the instance canvas.
   *
   * @param {number} now
   * @returns {boolean} whether the instance wants further frames
   */
  draw(now) {
    if (!this.#visible || !this.#context || !this.#width || !this.#height) return false;

    const animated = !this.#static && this.#settings.speed > 0;
    if (!animated && !this.#needsRender) return false;

    const { gl, canvas, uniforms } = renderer;
    if (!gl || !canvas) return false;

    if (canvas.width < this.#width || canvas.height < this.#height) {
      canvas.width = Math.max(canvas.width, this.#width);
      canvas.height = Math.max(canvas.height, this.#height);
    }

    const program = renderer.getProgram();
    if (!program) return false;

    gl.useProgram(program);
    if (!renderer.buffer) {
      renderer.buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, renderer.buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    } else {
      gl.bindBuffer(gl.ARRAY_BUFFER, renderer.buffer);
    }
    gl.enableVertexAttribArray(uniforms.position);
    gl.vertexAttribPointer(uniforms.position, 2, gl.FLOAT, false, 0, 0);
    gl.viewport(0, canvas.height - this.#height, this.#width, this.#height);

    const speed = this.#static ? 0 : this.#settings.speed;
    gl.uniform1f(uniforms.time, ((now - renderer.startTime) / 1000) * speed);
    gl.uniform2f(uniforms.resolution, this.#width, this.#height);
    gl.uniform1f(uniforms.grain, this.#settings.grain);
    gl.uniform1f(uniforms.relief, this.#settings.relief);
    gl.uniform1f(uniforms.nacre, this.#settings.nacre);
    gl.uniform1f(uniforms.contrast, this.#settings.contrast);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    this.#context.drawImage(canvas, 0, 0, this.#width, this.#height, 0, 0, this.#width, this.#height);
    this.#needsRender = false;
    return animated;
  }
}

if (!customElements.get('silk-hero-component')) {
  customElements.define('silk-hero-component', SilkHeroComponent);
}
